// M10-16162: a PO Ref No typed in (not picked from the dropdown) may be only the last few digits of a
// real PO, already consumed, or non-existent — ask the user to verify it once before submitting the
// invoice header, and suggest POs from the available list whose number ends with what was typed.
import { useEffect, useRef, useState } from "react";
import crossIcon from "src/assets/cross.svg";
import { Button, Modal } from "src/components";

const normalizePoRef = (value: any) => String(value ?? "").replace(/\s+/g, "").toUpperCase();

// Tracks whether the current manually-typed PO Ref No has been verified; resets whenever the value changes.
export const usePoRefVerification = (poRefNumber: string) => {
  const [isOpen, setIsOpen] = useState(false);
  const [matches, setMatches] = useState<any[]>([]);
  const isVerifiedRef = useRef(false);

  useEffect(() => {
    isVerifiedRef.current = false;
  }, [poRefNumber]);

  return {
    isOpen,
    matches,
    isVerified: () => isVerifiedRef.current,
    // fetchPoList is the (cached) PO Ref No listing used by the dropdown
    open: async (fetchPoList: () => Promise<any>) => {
      const typed = normalizePoRef(poRefNumber);
      let list: any[] = [];
      try {
        const res = await fetchPoList();
        list = res?.data?.data || [];
      } catch (error) {
        console.log(error);
      }
      setMatches(list.filter((po) => normalizePoRef(po?.poid_ref_number).endsWith(typed)));
      setIsOpen(true);
    },
    close: () => setIsOpen(false),
    markVerified: () => {
      isVerifiedRef.current = true;
      setIsOpen(false);
    },
  };
};

interface VerifyPoRefModalProps {
  isOpen: boolean;
  poRefNumber: string;
  matches: any[];
  onClose: () => void;
  onConfirm: () => void;
  onSelectMatch: (po: any) => void;
}

const VerifyPoRefModal = ({ isOpen, poRefNumber, matches, onClose, onConfirm, onSelectMatch }: VerifyPoRefModalProps) => (
  <Modal isOpen={isOpen} isHeader={false} onCloseModal={onClose}>
    <div className="flex flex-col gap-y-2 mb-4 w-[400px] mx-auto px-6 bg-white p-4 rounded-xl">
      <div className="flex justify-between items-center">
        <p className="text-normal py-1 font-semibold text-[#49484C]">Verify PO Ref No</p>
        <Button className="bg-transparent -mr-3" onClick={onClose}>
          <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
        </Button>
      </div>
      <p className="text-sm text-[#666666]">
        PO Ref No <span className="font-semibold text-[#8051FF]">{poRefNumber?.trim()}</span> was entered manually and is not
        selected from the available PO list. It may be a partial number, already consumed or may not exist. Please verify the PO
        number before proceeding.
      </p>
      {matches.length > 0 && (
        <div className="mt-1">
          <p className="text-sm font-medium text-[#49484C] mb-1">Matching PO(s) in the available list — select the correct one:</p>
          <div className="flex flex-col gap-y-1 max-h-[160px] overflow-y-auto">
            {matches.map((po) => (
              <button
                key={po?.stockist_po_attachment_id ?? po?.poid_ref_number}
                type="button"
                className="text-left text-sm px-3 py-2 border border-[#D0D0D0] rounded-md hover:bg-[#F3EEFF] hover:border-[#8051FF]"
                onClick={() => onSelectMatch(po)}
              >
                {po?.poid_ref_number}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-x-2 mt-2">
        <Button className="font-normal bg-gray-200 border border-gray-300" onClick={onClose}>
          Edit PO Ref No
        </Button>
        <Button className="font-normal text-white bg-[#8051FF]" onClick={onConfirm}>
          Verified, Continue
        </Button>
      </div>
    </div>
  </Modal>
);

export default VerifyPoRefModal;
